<template>
    <div class="admin-page-table" :class="{ 'admin-page-table_dense': dense }">
        <div class="admin-page-table__head" v-if="options && options.columns">
            <template v-for="(column, index) in options.columns">
                <!-- Класс колонки применяется и к заголовку: иначе заголовок переносится
                     там, где значение под ним стоит в одну строку, и они расходятся. -->
                <div
                    class="admin-page-table__cell"
                    :class="column.className"
                    :style="getColumnStyle(column)"
                    :key="index"
                >
                    {{ column.label }}
                </div>
            </template>

            <template v-if="defaultColumnSize && (options.editable || options.deletable)">
                <div class="admin-page-table__cell" :style="{ width: getColumnSize() }">Действия</div>
            </template>
        </div>

        <VScroll class="admin-page-table__body" @reach-end="$emit('next-page')">
            <template v-for="(row, rowIndex) in rows">
                <div
                    class="admin-page-table__row"
                    :class="[
                        { 'admin-page-table__row_last': rowIndex + 1 == rows.length },
                        { 'admin-page-table__row_selected': isSelected(row) },
                        rowClass ? rowClass(row) : '',
                    ]"
                    :key="rowIndex"
                    @click="$emit('click', row)"
                >
                    <template v-for="(column, columnIndex) in options.columns">
                        <div
                            class="admin-page-table__cell"
                            :class="column.className"
                            :style="getColumnStyle(column)"
                            :key="columnIndex"
                        >
                            <!-- badge вместо текста: колонка сама решает, рисовать ли
                                 пилюлю и каким цветом. Обычные колонки этого поля
                                 не имеют и рендерятся как раньше. -->
                            <span
                                class="admin-page-table__badge"
                                :style="{ '--badge-color': column.badge(row).color }"
                                v-if="column.badge && column.badge(row)"
                            >
                                {{ column.badge(row).label }}
                            </span>

                            <template v-else>{{ column.display(row) }}</template>
                        </div>
                    </template>

                    <template v-if="options && defaultColumnSize && (options.editable || options.deletable)">
                        <div
                            class="admin-page-table__cell admin-page-table__cell_action"
                            :style="{ width: getColumnSize() }"
                        >
                            <button
                                class="admin-page-table__action"
                                v-if="isEditable(options.editable, row)"
                                @click.stop="$emit('edit', row)"
                            >
                                <EditSvg></EditSvg>
                            </button>

                            <button
                                class="admin-page-table__action admin-page-table__action_delete"
                                v-if="isDeletable(options.deletable, row)"
                                @click.stop="$emit('delete', row)"
                            >
                                <DeleteSvg></DeleteSvg>
                            </button>
                        </div>
                    </template>
                </div>
            </template>

            <!-- Встроенный пейджер можно отключить: на странице оборудования под таблицей
                 стоит полный футер со счётчиком и выбором числа строк, и два пейджера
                 подряд сбивали бы. -->
            <PagePaginate
                class="admin-page-table__paginator"
                v-if="!hidePaginator"
                :count="limit"
                :total="total"
                :value="page"
                @input="(value) => $emit('page', value)"
            ></PagePaginate>
        </VScroll>
    </div>
</template>
<script>
// Svg
import EditSvg from '@/images/edit.svg';
import DeleteSvg from '@/images/delete.svg';

// Components
import VButton from './VButton/VButton.vue';
import PagePaginate from './PagePaginate/PagePaginate.vue';
import VScroll from '@/views/components/VScroll/VScroll.vue';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';

@Component({
    components: {
        EditSvg,
        DeleteSvg,

        VButton,
        PagePaginate,
        VScroll,
    },
})
class VContentTableDefault extends Vue {
    @Prop({ type: Array, default: () => [] })
    rows;

    @Prop({ type: Number, default: 1 })
    page;

    @Prop({ type: Number, default: 1 })
    total;

    @Prop({ type: Number, default: 15 })
    limit;

    @Prop({ type: Object, default: () => {} })
    options;

    // Плотная раскладка: вертикальные отступы строк вдвое меньше. По умолчанию выключена,
    // чтобы не менять таблицы админки, которые на этот компонент и рассчитаны.
    @Prop({ type: Boolean, default: false })
    dense;

    // Дополнительный класс строки — например, подсветка открытой карточки.
    @Prop({ type: Function, default: null })
    rowClass;

    // Идентификатор выделенной строки. Сравнение строковое: в маршруте параметр приходит
    // строкой, а в данных лежит число.
    @Prop({ type: [String, Number], default: null })
    selectedId;

    @Prop({ type: Boolean, default: false })
    hidePaginator;

    isSelected(row) {
        return this.selectedId !== null && String(this.selectedId) === String(row.id);
    }

    isCallable(value) {
        return typeof value === 'function';
    }

    isEditable(editable, value) {
        if (this.isCallable(editable)) {
            return editable(value);
        }

        return !!editable;
    }

    isDeletable(deletable, value) {
        if (this.isCallable(deletable)) {
            return deletable(value);
        }

        return !!deletable;
    }

    getColumnSize(column) {
        var size = column?.size ?? this.defaultColumnSize;

        return size + '%';
    }

    /**
     * Ширина колонки и её нижняя граница.
     *
     * Доли в процентах распределяют свободное место, но ничего не обещают про читаемость:
     * в узкой области «Серийный номер» ужимается до пары символов, а заголовок и значение
     * начинают слипаться. minWidth — та ширина, ниже которой колонка не сжимается;
     * дальше таблица уезжает в горизонтальную прокрутку, а не в кашу.
     *
     * Колонки без minWidth ведут себя как раньше — таблицы админки на этот компонент
     * рассчитаны и трогать их незачем.
     */
    getColumnStyle(column) {
        const style = { width: this.getColumnSize(column) };
        const minWidth = column?.minWidth;

        if (minWidth) {
            style.minWidth = typeof minWidth === 'number' ? `${minWidth}px` : minWidth;
        }

        return style;
    }

    get defaultColumnSize() {
        let columns = this.options?.columns?.length ?? 0;
        let occupied = 0;

        if (this.options.editable || this.options.deletable) {
            columns = columns + 1;
        }

        if (this.options && Array.isArray(this.options.columns)) {
            occupied = this.options.columns.reduce((sum, column) => {
                if (column.size) {
                    columns = columns - 1;
                    sum = sum + column.size;
                }

                return sum;
            }, 0);
        }

        if (occupied > 100) {
            return 0;
        }

        return (100 - occupied) / columns;
    }
}

export default VContentTableDefault;
</script>
<style lang="scss">
:root {
    --admin-page-table-head-color: #808080;
    --admin-page-table-border-color: #e5e4e5;
    --admin-page-table-background-color: var(--color-white);
    --admin-page-table-row-hover-color: #f0f0f0;
}

.admin-page-table {
    height: 100%;
    overflow: hidden;

    &__row,
    &__head {
        display: flex;
        margin: 0 36px 0 36px;
        padding: 30px 24px;
        border-bottom: 1px solid var(--admin-page-table-border-color);
        background-color: var(--admin-page-table-background-color);
    }

    &__row {
        cursor: pointer;

        &_last {
            border-bottom-left-radius: 18px;
            border-bottom-right-radius: 18px;
            border: none;
        }

        &:hover {
            background-color: var(--admin-page-table-row-hover-color);
        }

        // Открытая в боковой панели строка: полоса слева, а не только заливка —
        // заливка сама по себе спорит с подсветкой при наведении.
        &_selected,
        &_selected:hover {
            background-color: #e8f5e9;
            box-shadow: inset 3px 0 0 0 var(--color-green);
        }
    }

    // Плотная раскладка — ориентир на список обращений: там строка живёт в одной
    // «высоте текста плюс воздух», а не в 30px сверху и снизу.
    &_dense {
        .admin-page-table__row,
        .admin-page-table__head {
            padding: 12px 24px;
        }
    }

    &__badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 10px;
        border-radius: 999px;
        background: var(--badge-color, #9aa0a6);
        color: #fff;
        font-size: 0.75rem;
        font-weight: 600;
        line-height: 1;
        white-space: nowrap;
    }

    &__cell {
        display: flex;
        width: 100%;
        align-items: center;

        &_pre-line {
            white-space: pre-line;
        }

        // Колонка в одну строку: номера, даты, серийники. Многоточию нужен блочный
        // контекст, поэтому ячейка перестаёт быть flex — выравнивание по центру
        // возвращает line-height, у однострочного текста этого достаточно.
        &_nowrap {
            display: block;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            line-height: 1.4;
        }
    }

    &__head {
        border-top-left-radius: 18px;
        border-top-right-radius: 18px;
        color: var(--admin-page-table-head-color);
    }

    // Высота шапки: 60px паддингов + 1px границы + строка текста. Строка — от размера
    // шрифта (1.5em = line-height body), а не числом: в крупном режиме текста шапка выше
    // 82px, и тело с фиксированным вычетом уходило бы низом под overflow: hidden таблицы.
    &__body {
        height: calc(100% - 61px - 1.5em) !important;
        padding-bottom: 24px;

        .v-scroll__thumb_y {
            right: 20px;
        }
    }

    &__action {
        width: 36px;
        height: 36px;
        margin: 0 12px 0 0;
        padding: 8px;

        &:hover {
            color: var(--color-green);
        }

        &:last-child {
            margin: 0;
        }

        &_delete {
            &:hover {
                color: var(--color-red);
            }
        }
    }

    &__paginator {
        width: auto;
        margin: 8px 21px 0 21px;
        justify-content: center;
    }
}
</style>
